import { absolutizeMediaUrl } from './url';

/**
 * Converts a PDF URL to an absolute URL
 * Accepts blob:, http(s), or server-relative paths
 */
export function absolutizePdfUrl(url: string | undefined): string {
  return absolutizeMediaUrl(url) || '';
}

interface FetchPdfOptions {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  timeout?: number;
}

/**
 * Fetches a PDF with retry logic and exponential backoff
 * Handles 404/5xx errors gracefully
 */
export async function fetchPdfWithRetry(
  url: string | undefined,
  options: FetchPdfOptions = {}
): Promise<string> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 8000,
    timeout = 30000,
  } = options;

  if (!url) {
    throw new Error('PDF URL is required');
  }

  const absoluteUrl = absolutizePdfUrl(url);

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(absoluteUrl, {
        method: 'HEAD',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        return absoluteUrl;
      }

      if (response.status === 404) {
        throw new Error('PDF not found (404)');
      }

      if (response.status >= 500 && attempt < maxRetries) {
        const delay = Math.min(initialDelay * Math.pow(2, attempt), maxDelay);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      throw new Error(`Failed to fetch PDF (${response.status})`);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('PDF fetch timeout');
      }

      if (attempt === maxRetries) {
        throw error;
      }

      const delay = Math.min(initialDelay * Math.pow(2, attempt), maxDelay);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw new Error('Failed to fetch PDF after all retries');
}
